import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Text,
  VStack,
  FormErrorMessage,
  CompanyNumberInput,
} from '@wegroup/design-system';
import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSetQueryParam from '../../../../../common/src/hooks/query-params/useAddQueryParam';
import { useCreatePartyFromCompanyRegistration } from '../../api/createPartyFromCompanyRegistration';
import {
  CompanyRegistration,
  CompanyRegistrationType,
} from '../../../../../parties/src/types';
import { usePatchParty } from '../../../../../parties/src/features/detail/api/patchParty';
import { isSocialLoginUrl } from '../../../../../app/src/features/authentication/constants/SocialAuth';
import useAuth from '../../../../../app/src/features/authentication/hooks/useAuth';
import { getTypeForCountry } from '../../../../../design-system/src/content/components/CompanyNumberInput';

interface Props {
  isOpen: boolean;
  onClose?: () => void;
  partyId?: string;
  handleOnNext?: (company_registration?: CompanyRegistration) => void;
  handleOnFinish?: () => void;
  allowContinueWithout?: boolean;
  description?: string;
  defaultCbe?: string;
  defaultCbeType?: CompanyRegistrationType;
}

interface FormValues {
  companyRegistration: CompanyRegistration;
}

const NoCbeModal: React.FC<React.PropsWithChildren<Props>> = ({
  isOpen,
  partyId,
  onClose,
  handleOnNext,
  handleOnFinish,
  allowContinueWithout,
  description,
  defaultCbe,
  defaultCbeType,
}) => {
  const { t } = useTranslation();
  const addQueryParam = useSetQueryParam();
  const { distribution } = useAuth();

  const {
    control,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    // resolver: yupResolver(object({ type: })),
    defaultValues: {
      companyRegistration: {
        nr: defaultCbe,
        type:
          defaultCbeType ||
          getTypeForCountry(distribution?.address?.country_code || 'BE'),
      },
    },
  });

  const {
    mutateAsync: createProspectFromCompanyRegistration,
    isLoading: isCreatingProspectByCompReg,
  } = useCreatePartyFromCompanyRegistration();
  const { mutateAsync: updateParty, isLoading } = usePatchParty();

  const handleOnContinue = handleSubmit(async (values) => {
    try {
      if (!partyId) {
        const resp = await createProspectFromCompanyRegistration(
          values.companyRegistration,
        );

        addQueryParam({
          party_id: resp.id,
        });

        handleOnFinish?.();
      } else if (!isSocialLoginUrl) {
        await updateParty({
          partyId,
          payload: {
            company_registration: values.companyRegistration,
          },
        });

        handleOnNext?.();
      } else {
        // social handling
        handleOnNext?.(values.companyRegistration);
      }
    } catch (_) {
      console.error('COMPANY-SCAN_CBE-LOOKUP_RESULT_WRONG-CBE');
    }
  });

  const companyRegistration = watch('companyRegistration');

  return (
    <Modal isOpen={isOpen} onClose={() => onClose?.()} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('COMPANY-SCAN_CBE-LOOKUP_RESULT_ADD-CBE')}</ModalHeader>
        {onClose && <ModalCloseButton />}
        <ModalBody as={VStack} spacing="4">
          <Text
            dangerouslySetInnerHTML={{
              __html: description || t('COMPANY-SCAN_CBE-LOOKUP_RESULT_NO-CBE'),
            }}
          />
          <FormControl isInvalid={!!errors.companyRegistration}>
            <FormLabel>{t('COMPANY-NUMBER')}</FormLabel>

            <Controller
              name="companyRegistration"
              control={control}
              render={({ field }) => (
                <CompanyNumberInput
                  defaultCountry={
                    distribution?.address?.country_code || undefined
                  }
                  {...field}
                />
              )}
            />
            {errors.companyRegistration?.message && (
              <FormErrorMessage>
                {errors.companyRegistration.message}
              </FormErrorMessage>
            )}
          </FormControl>
        </ModalBody>

        <ModalFooter>
          {onClose && (
            <Button variant="ghost" mr={3} onClick={onClose}>
              {t('CLOSE')}
            </Button>
          )}

          {allowContinueWithout ? (
            <Button
              variant="primary"
              onClick={companyRegistration ? handleOnContinue : handleOnFinish}
              isLoading={isLoading || isCreatingProspectByCompReg}
            >
              {companyRegistration ? t('CONTINUE') : t('CONTINUE-WITHOUT')}
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={handleOnContinue}
              isLoading={isLoading || isCreatingProspectByCompReg}
              isDisabled={!companyRegistration}
            >
              {t('CONTINUE')}
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default NoCbeModal;
